import {
  ArrowUpIcon,
  ArrowDownIcon,
  DollarSign,
  TrendingUp,
  Percent,
  TrendingUpDown,
} from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { SummaryHeader } from "../../ui/SummaryHeader";
import ManagerialLoader from "../../../components/loaders/ManagerialLoader";
import { useWholesaleManagerial } from "../hooks/useWholesaleManagerial";
import { useMonthlyForecastWholesale } from "../hooks/useWholesaleForecastMonthly";
import { formatCurrency } from "../../../utils/formatCurrency";
import { DataFreshness } from "../../../components/feedback/DataFreshness";
import { isIncompleteCurrentMonth } from "../../../utils/dateUtils";

export function WholesaleManagerialMetrics({ selectedMonth }: { selectedMonth: string }) {
  const { data, isLoading, error } = useWholesaleManagerial(selectedMonth);
  const { data: wholesaleMonthlyForecast, isLoading: isMonthlyForecastLoading } = useMonthlyForecastWholesale(selectedMonth);

  if (isLoading || isMonthlyForecastLoading) {
    return (
      <>
        <SummaryHeader title="Quick Summary" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <ManagerialLoader variant="revenue-card" />
          <ManagerialLoader variant="profit-card" />
          <ManagerialLoader variant="forecast-card" />
          <ManagerialLoader variant="margin-card" />       
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 text-red-500">
        Failed to load managerial dashboard data.
      </div>
    );
  }

  const actual = wholesaleMonthlyForecast.wholesale_revenue;
  const forecast = wholesaleMonthlyForecast.forecast_value;
  const revenueChange = data.monthly_revenue.growth_rate * 100;
  const profitChange = data.monthly_profit.growth_rate * 100;
  const marginChange = data.profit_margin.growth_rate * 100;
  
  // Only show trends if month is complete AND there's actual data
  const hasRevenueData = data.monthly_revenue.total_revenue > 0;
  const hasProfitData = data.monthly_profit.total_profit > 0;
  const showTrend = !isIncompleteCurrentMonth(selectedMonth) && hasRevenueData;

  return (
    <>
      <SummaryHeader title="Quick Summary" />
      <DataFreshness monthString={selectedMonth} className="mb-4" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Revenue */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between pb-2">
              <p className="text-sm font-medium text-muted-foreground">
                Monthly Revenue
              </p>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">
              {formatCurrency(data.monthly_revenue.total_revenue)}
            </div>
            {showTrend && (
              <div className="flex items-center pt-1">
                <span
                  className={`text-xs ${
                    revenueChange >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {revenueChange >= 0 ? (
                    <ArrowUpIcon className="mr-1 inline h-3 w-3" />
                  ) : (
                    <ArrowDownIcon className="mr-1 inline h-3 w-3" />
                  )}
                  {Math.abs(revenueChange).toFixed(1)}%
                </span>
                <span className="text-xs text-muted-foreground ml-1">
                  from last month
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Profit */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between pb-2">
              <p className="text-sm font-medium text-muted-foreground">
                Monthly Profit
              </p>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">
              {formatCurrency(data.monthly_profit.total_profit)}
            </div>
            {showTrend && hasProfitData && (
              <div className="flex items-center pt-1">
                <span
                  className={`text-xs ${
                    profitChange >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {profitChange >= 0 ? (
                    <ArrowUpIcon className="mr-1 inline h-3 w-3" />
                  ) : (
                    <ArrowDownIcon className="mr-1 inline h-3 w-3" />
                  )}
                  {Math.abs(profitChange).toFixed(1)}%
                </span>
                <span className="text-xs text-muted-foreground ml-1">
                  from last month
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Forecast Accuracy */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between pb-2">
              <p className="text-sm font-medium text-muted-foreground">
                Monthly Forecast
              </p>
              <TrendingUpDown className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">
              {formatCurrency(forecast)}
            </div>
            <div className="flex items-center pt-1">
              {typeof actual === "number" && typeof forecast === "number" && forecast !== 0 ? (
                (() => {
                  const accuracy = wholesaleMonthlyForecast.forecast_accuracy;
                  if (accuracy === 0) {
                    return (
                      <span className="text-xs font-semibold flex items-center text-muted-foreground">
                        Pending month completion
                      </span>
                    );
                  }
                  const diff = actual - forecast;
                  const percentDiff = (diff / forecast) * 100;
                  const absPercent = Math.abs(percentDiff).toFixed(2);
                  const absDollar = Math.abs(diff).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
                  const isOver = percentDiff > 1;
                  const isUnder = percentDiff < -1;
                  const isOnTarget = !isOver && !isUnder; // within ±1%
                  if (isOnTarget) {
                    return (
          <span className="text-xs font-semibold flex items-center text-muted-foreground">
             On Target
          </span>
        );
      }
                  return (
                    <span
                      className={`text-xs font-semibold flex items-center ${
                        isOver ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      Actual: {absPercent}% {isOver ? "Over" : "Under"} Forecast
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({isOver ? "+" : "-"}${absDollar})
                      </span>
                    </span>
                  );
                })()
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Profit Margin */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between pb-2">
              <p className="text-sm font-medium text-muted-foreground">
                Profit Margin
              </p>
              <Percent className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">
              {data.profit_margin.profit_margin.toFixed(2)}%
            </div>
            {showTrend && (
              <div className="flex items-center pt-1">
                <span
                  className={`text-xs ${
                    marginChange >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {marginChange >= 0 ? (
                    <ArrowUpIcon className="mr-1 inline h-3 w-3" />
                  ) : (
                    <ArrowDownIcon className="mr-1 inline h-3 w-3" />
                  )}
                  {Math.abs(marginChange).toFixed(2)}%
                </span>
                <span className="text-xs text-muted-foreground ml-1">
                  from last month
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        
      </div>
    </>
  );
}
